






























import { computed, defineComponent, PropType } from "@vue/composition-api";
import { PayeeInformation } from "@scrile/api-provider/dist/api/PayeeInformationProvider";
import { PersonalInformation } from "@scrile/api-provider/dist/api/PersonalInformationProvider";
import { TagInfo } from "@scrile/api-provider/dist/api/TagsProvider";
import { Gender } from "@scrile/api-provider/dist/api/UserProvider";
import {
  PreferredGender,
  RelationshipRole,
  UserPublicInformation,
} from "@scrile/api-provider/dist/api/UserPublicDataProvider";
import { UserVerification } from "@scrile/api-provider/dist/api/UserVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { $t } from "@/lib/i18nHelpers";
import { getLocationName } from "@/lib/location";
import { formatDateOnly } from "@/lib/time";
import { genderNameMap, preferredGenderNameMap, relationshipRoleNameMap } from "@/const";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import PageTitle from "@/components/PageTitle";
import getComponent from "../integrations";

interface Info {
  title: string;
  items: InfoItem[];
}

interface InfoItem {
  name: string;
  value: string | null;
  icon?: string;
  class?: string;
}

export default defineComponent({
  name: "PayeeInfoView",
  components: { PageTitle, BaseIcon, BaseIconButton },
  props: {
    personalInformation: { type: Object as PropType<PersonalInformation>, default: null },
    publicInformation: { type: Object as PropType<UserPublicInformation>, default: null },
    payeeInformation: { type: Object as PropType<PayeeInformation>, default: null },
    userVerification: { type: Object as PropType<UserVerification>, default: null },
    userTags: { type: Array as PropType<string[]>, default: () => [] },
    userContentTypes: { type: Array as PropType<string[]>, default: () => [] },
    tags: { type: Array as PropType<TagInfo[]>, default: () => [] },
  },
  setup(props) {
    const { authUserStrict, isFullCreator } = useAuthUser();

    const getPayeeInformation = (): Info | null => {
      if (!props.payeeInformation) return null;
      const integration = getComponent(props.payeeInformation.payoutSystem);
      const listField = integration.listField;
      const formFields = integration.formFields;
      const systemInfo: InfoItem[] = formFields.reduce<InfoItem[]>((out, field) => {
        if (props.payeeInformation?.payoutSystem) {
          out.push({
            name: field.label ?? "",
            value: (props.payeeInformation as any)[props.payeeInformation.payoutSystem][field.name],
          });
        }
        return out;
      }, [] as InfoItem[]);

      return {
        title: $t("Payout system"),
        items: [
          {
            name: $t("Payout system"),
            value: listField?.text ?? "",
          },
          {
            name: $t("Currency"),
            value: props.payeeInformation.currency,
          },
          ...systemInfo,
        ],
      };
    };

    const info = computed<Info[]>(() => {
      if (!props.personalInformation || !props.userVerification || !props.publicInformation) return [];
      const payeeInfo = getPayeeInformation();
      return [
        {
          title: $t("Personal information"),
          items: [
            {
              name: $t("Legal first name"),
              value: props.personalInformation.firstName,
            },
            {
              name: $t("Legal middle name"),
              value: props.personalInformation.middleName ?? $t("Not provided"),
              class: props.personalInformation.middleName ? undefined : "gray-text",
            },
            {
              name: $t("Legal last name"),
              value: props.personalInformation.lastName,
            },
            {
              name: $t("Date of birth"),
              value: props.publicInformation.dateOfBirth
                ? formatDateOnly(props.publicInformation.dateOfBirth, "MM/dd/yyyy")
                : null,
            },
            {
              name: $t("Phone number"),
              value: props.personalInformation.phone,
            },
          ],
        },
        {
          title: $t("Identification"),
          items: [
            {
              name: $t("Front side of your ID"),
              value: props.userVerification.idPhoto ? $t("Attached") : $t("Not attached"),
              icon: props.userVerification.idPhoto ? "sci-lock" : undefined,
            },
            {
              name: $t("Back side of your ID"),
              value: props.userVerification.idPhotoBack ? $t("Attached") : $t("Not attached"),
              icon: props.userVerification.idPhotoBack ? "sci-lock" : undefined,
            },
            {
              name: $t("Selfie holding your ID"),
              value: props.userVerification.photoWithId ? $t("Attached") : $t("Not attached"),
              icon: props.userVerification.photoWithId ? "sci-lock" : undefined,
            },
          ],
        },
        {
          title: $t("About you"),
          items: [
            {
              name: $t("Home location"),
              value: getLocationName(props.publicInformation.location),
            },
            {
              name: $t("Gender"),
              value: genderNameMap[authUserStrict.value.gender] ?? $t("Not provided"),
              class: authUserStrict.value.gender === Gender.UNDEFINED ? "gray-text" : undefined,
            },
            {
              name: $t("Who you want"),
              value: preferredGenderNameMap[props.publicInformation.preferredGender] ?? $t("Not provided"),
              class: props.publicInformation.preferredGender === PreferredGender.UNDEFINED ? "gray-text" : undefined,
            },
            {
              name: $t("Preferred position"),
              value: relationshipRoleNameMap[props.publicInformation.preferredPosition] ?? $t("Not provided"),
              class: props.publicInformation.preferredPosition === RelationshipRole.UNDEFINED ? "gray-text" : undefined,
            },
          ],
        },
        {
          title: $t("Your focus areas"),
          items: [
            {
              name: $t("Categories"),
              value: props.userTags.length
                ? props.tags
                    .filter(i => props.userTags.includes(i.id))
                    .map(i => i.title)
                    .join(", ")
                : $t("Not provided"),
              class: props.userTags.length ? undefined : "gray-text",
            },
            {
              name: $t("Content type"),
              value: props.userContentTypes.length
                ? props.tags
                    .filter(i => props.userContentTypes.includes(i.id))
                    .map(i => i.title)
                    .join(", ")
                : $t("Not provided"),
              class: props.userContentTypes.length ? undefined : "gray-text",
            },
          ],
        },
        ...(payeeInfo ? [payeeInfo] : []),
      ];
    });

    return {
      info,
      isFullCreator,
    };
  },
});
